// StatsAndMilestones.jsx

import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  Grow,
  Slide,
} from "@mui/material";
import CountUp from "react-countup";
import { styled, keyframes } from "@mui/system";

// Import icons
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

// Keyframe animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const rotateIn = keyframes`
  0% { transform: rotate(-30deg) scale(0.8); opacity: 0; }
  100% { transform: rotate(0deg) scale(1); opacity: 1; }
`;

// Styled components
const AnimatedIcon = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  marginBottom: theme.spacing(1),
  transition: "all 0.5s ease",
  "&.active": {
    animation: `${rotateIn} 0.7s forwards`,
  },
}));

const CounterWrapper = styled(Box)(({ theme }) => ({
  textAlign: "left",
  padding: theme.spacing(2),
  position: "relative",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  borderRadius: "8px",
  backgroundColor: "#fff",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 10px 20px rgba(191, 10, 48, 0.1)",
    "& .counter-value": {
      animation: `${pulse} 1s infinite`,
    },
  },
}));

// Simple fade-in on scroll
function ScrollFadeIn({ children, index = 0 }) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <Box ref={sectionRef} sx={{ overflow: "hidden" }}>
      <Slide
        direction="up"
        in={isVisible}
        timeout={500}
        style={{ transitionDelay: `${index * 200}ms` }}
      >
        <Box>{children}</Box>
      </Slide>
    </Box>
  );
}

function CounterBox({ icon: Icon, end, label, index, isVisible }) {
  const delayedVisible = isVisible && index < 10; // basic stagger

  return (
    <Grow
      in={delayedVisible}
      timeout={800}
      style={{ transitionDelay: `${index * 200}ms` }}
    >
      <CounterWrapper>
        <AnimatedIcon className={delayedVisible ? "active" : ""}>
          <Icon sx={{ fontSize: 48, color: "#BF0A30", mr: 2 }} />
          <Box>
            <Typography
              variant="h5"
              fontWeight="bold"
              className="counter-value"
              sx={{ display: "inline-block", color: "#333" }}
            >
              {delayedVisible && (
                <CountUp end={end} duration={2.5} separator="," />
              )}
            </Typography>
            <Typography variant="body1" sx={{ color: "#555" }}>
              {label}
            </Typography>
          </Box>
        </AnimatedIcon>
      </CounterWrapper>
    </Grow>
  );
}

export default function StatsAndMilestones2() {
  const [sectionVisibility, setSectionVisibility] = useState({
    section1: false,
    section2: false,
    section3: false,
    section4: false,
  });

  // Refs for each section
  const sectionRefs = {
    section1: useRef(null),
    section2: useRef(null),
    section3: useRef(null),
    section4: useRef(null),
  };

  // Intersection observers
  useEffect(() => {
    const observers = {};

    Object.entries(sectionRefs).forEach(([key, ref]) => {
      observers[key] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setSectionVisibility((prev) => ({ ...prev, [key]: true }));
            observers[key].unobserve(entry.target);
          }
        },
        { threshold: 0.15 }
      );

      if (ref.current) {
        observers[key].observe(ref.current);
      }
    });

    return () => {
      Object.entries(observers).forEach(([key, observer]) => {
        if (sectionRefs[key].current) {
          observer.unobserve(sectionRefs[key].current);
        }
      });
    };
  }, []);

  // Heading style
  const headingSx = {
    textTransform: "uppercase",
    fontWeight: 700,
    color: "#BF0A30",
    mb: 2,
  };

  // Divider style
  const dividerSx = {
    width: 80,
    height: 4,
    mb: 4,
    backgroundColor: "#BF0A30",
    borderRadius: 2,
    mx: "auto",
  };

  return (
    <Box sx={{ py: 6, backgroundColor: "#f5f5f5" }}>
      <Container maxWidth="lg">

        {/* SECTION 3: Financial Performance */}
        <Box ref={sectionRefs.section3} sx={{ mb: 8 }}>
          <ScrollFadeIn index={0}>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={headingSx}
            >
              Financial Performance
            </Typography>
            <Divider sx={dividerSx} />
            <Grid container spacing={4} justifyContent="flex-start">
              <Grid item xs={12} sm={6} md={3}>
                <CounterBox
                  icon={AttachMoneyIcon}
                  end={50}
                  label="Annual Turnover (in Cr)"
                  index={0}
                  isVisible={sectionVisibility.section3}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CounterBox
                  icon={ShowChartIcon}
                  end={12}
                  label="Profit Margin (%)"
                  index={1}
                  isVisible={sectionVisibility.section3}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CounterBox
                  icon={ShowChartIcon}
                  end={25}
                  label="Market Share (%)"
                  index={2}
                  isVisible={sectionVisibility.section3}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CounterBox
                  icon={BusinessCenterIcon}
                  end={15}
                  label="ROI (%)"
                  index={3}
                  isVisible={sectionVisibility.section3}
                />
              </Grid>
            </Grid>
          </ScrollFadeIn>
        </Box>

        {/* SECTION 4: Customer & Market Impact */}
        <Box ref={sectionRefs.section4}>
          <ScrollFadeIn index={0}>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={headingSx}
            >
              Customer & Market Impact
            </Typography>
            <Divider sx={dividerSx} />
            <Grid container spacing={4} justifyContent="flex-start">
              <Grid item xs={12} sm={6} md={3}>
                <CounterBox
                  icon={GroupIcon}
                  end={50}
                  label="Key Clients"
                  index={0}
                  isVisible={sectionVisibility.section4}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CounterBox
                  icon={EmojiEventsIcon}
                  end={8}
                  label="Awards Received"
                  index={1}
                  isVisible={sectionVisibility.section4}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CounterBox
                  icon={CheckCircleIcon}
                  end={40}
                  label="Repeat Customers"
                  index={3}
                  isVisible={sectionVisibility.section4}
                />
              </Grid>
            </Grid>
          </ScrollFadeIn>
        </Box>
      </Container>
    </Box>
  );
}